import React from 'react';

import moment from 'moment-tz';
import {useSelector} from 'react-redux';
import {WithRouterProps} from 'react-router';

import {AccountPageShell} from 'app/account/components/AccountPageShell';
import {
  LoadingBreadcrumbName,
  LoadingLine,
} from 'app/general/loading/DetailLoadingPage';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {useReportDetail, useReportRunDetail} from 'app/reports/api';
import {renderers} from 'app/reports/components/renderers';
import {getReportBySlugSelector} from 'app/reports/slice';
import {REPORTSRUN_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

export function ReportsRunPage({
  params,
}: WithRouterProps<{slug: string; runid: string}, unknown>): JSX.Element {
  const report = useSelector(getReportBySlugSelector(params.slug));
  const {isLoading: isLoadingReport, error: reportError} = useReportDetail(
    params.slug
  );

  const {
    data: reportResult,
    error: reportRunError,
    isLoading: isLoadingReportRun,
  } = useReportRunDetail(params.runid);

  const formattedParams =
    reportResult != null
      ? `${moment(reportResult.params.starts_at).format('ll')} - ${moment(
          reportResult.params.ends_at
        ).format('ll')}`
      : null;

  return (
    <AccountPageShell
      location="reports"
      title={report?.name}
      error={reportError || reportRunError}
      isLoading={isLoadingReport || isLoadingReportRun}
      errorContent={{head: gt('Could not load the report')}}
      content={
        report != null && reportResult != null
          ? {report, reportResult}
          : undefined
      }
      titleNode={
        report?.name ? (
          <div className="row">
            <div className="col-12 col-sm">
              <h1>{report?.name}</h1>
              <p className="lead">
                {formattedParams ?? (
                  <div className="pt-2 pb-3 border-bottom">
                    <LoadingLine height="1.25rem" width="30%" />
                  </div>
                )}
              </p>
            </div>
            {reportResult != null ? (
              <div className="col-auto d-flex align-items-center pb-3 pb-sm-0">
                <a
                  href={`${REPORTSRUN_API_URL}${params.runid}/export/`}
                  className="btn btn-primary px-3"
                >
                  {gt('Download')}
                </a>
              </div>
            ) : null}
          </div>
        ) : null
      }
      additionalBreadcrumbProps={{
        params: formattedParams ?? <LoadingBreadcrumbName />,
      }}
    >
      {({report, reportResult}) => renderers[report.slug](reportResult)}
    </AccountPageShell>
  );
}

export const ReportsResultBreadcrumb = ({
  params,
  ...props
}: {
  params: string;
}) => <BreadcrumbItemStructure {...props} text={params} />;
